import { useQuery, useInfiniteQuery, UseQueryResult } from '@tanstack/react-query';
import { list_bands, Band, list_venues, Venue, list_all_shows, Show, list_musicians, Musician, view_band, view_musician, view_venue, view_show, list_band_shows, view_band_musicians, list_band_genres, BandGenre, list_band_show_comments, ShowComment, list_band_show_media, ShowMedia, list_musician_bands, list_musician_shows, list_musician_show_comments, list_musician_show_media, list_musician_links, MusicianLink, list_venue_shows, list_venue_show_comments, list_venue_show_media, list_venue_bands, list_users, FirebaseUser, list_show_contributors, ShowContributorRecord, list_contributor_bands, get_my_musician, get_fan_profile, FanProfile, list_shows_paginated, PaginatedShowsResponse, get_network_graph, NetworkGraphResponse } from './index';

/**
 * React Query hook to fetch all bands
 * @returns UseQueryResult with bands data, loading, and error states
 */
export const useBands = (): UseQueryResult<Band[], Error> => {
  return useQuery({
    queryKey: ['bands'],
    queryFn: list_bands,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
  });
};

/**
 * React Query hook to fetch all venues
 * @returns UseQueryResult with venues data, loading, and error states
 */
export const useVenues = (): UseQueryResult<Venue[], Error> => {
  return useQuery({
    queryKey: ['venues'],
    queryFn: list_venues,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
  });
};

/**
 * React Query hook to fetch all shows
 * @returns UseQueryResult with shows data, loading, and error states
 */
export const useShows = (): UseQueryResult<Show[], Error> => {
  return useQuery({
    queryKey: ['shows'],
    queryFn: list_all_shows,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
  });
};

// Page param type for infinite shows query
type ShowsPageParam = {
  cursor: string | undefined;
  direction: 'future' | 'past' | 'around';
};

/**
 * React Query hook for infinite scrolling shows (bi-directional pagination)
 * @param initialLimit - Number of shows to fetch per page (default: 20)
 * @returns UseInfiniteQueryResult with paginated shows data
 */
export const useInfiniteShows = (initialLimit = 20) => {
  return useInfiniteQuery({
    queryKey: ['shows', 'infinite'],
    queryFn: async ({ pageParam }: { pageParam: ShowsPageParam }) => {
      const { cursor, direction } = pageParam;
      return list_shows_paginated({ cursor, direction, limit: initialLimit });
    },
    initialPageParam: { cursor: undefined, direction: 'around' } as ShowsPageParam,
    getNextPageParam: (lastPage: PaginatedShowsResponse): ShowsPageParam | undefined => {
      if (!lastPage.hasMore || !lastPage.nextCursor) return undefined;
      return { cursor: lastPage.nextCursor, direction: 'future' };
    },
    getPreviousPageParam: (firstPage: PaginatedShowsResponse): ShowsPageParam | undefined => {
      if (!firstPage.hasPrev || !firstPage.prevCursor) return undefined;
      return { cursor: firstPage.prevCursor, direction: 'past' };
    },
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
};

/**
 * React Query hook to fetch all musicians
 * @returns UseQueryResult with musicians data, loading, and error states
 */
export const useMusicians = (): UseQueryResult<Musician[], Error> => {
  return useQuery({
    queryKey: ['musicians'],
    queryFn: list_musicians,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes (previously cacheTime)
  });
};

/**
 * React Query hook to fetch a single band by ID
 * @param id - The band ID to fetch
 * @returns UseQueryResult with band data, loading, and error states
 */
export const useBand = (id: string | undefined): UseQueryResult<Band | null, Error> => {
  return useQuery({
    queryKey: ['band', id],
    queryFn: () => view_band(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch a single musician by ID
 * @param id - The musician ID to fetch
 * @returns UseQueryResult with musician data, loading, and error states
 */
export const useMusician = (id: string | undefined): UseQueryResult<Musician | null, Error> => {
  return useQuery({
    queryKey: ['musician', id],
    queryFn: () => view_musician(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch a single venue by ID
 * @param id - The venue ID to fetch
 * @returns UseQueryResult with venue data, loading, and error states
 */
export const useVenue = (id: string | undefined): UseQueryResult<Venue | null, Error> => {
  return useQuery({
    queryKey: ['venue', id],
    queryFn: () => view_venue(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch a single show by ID
 * @param id - The show ID to fetch
 * @returns UseQueryResult with show data, loading, and error states
 */
export const useShow = (id: string | undefined): UseQueryResult<Show | null, Error> => {
  return useQuery({
    queryKey: ['show', id],
    queryFn: () => view_show(id!),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch shows for a specific band
 * @param bandId - The band ID to fetch shows for
 * @returns UseQueryResult with shows data, loading, and error states
 */
export const useBandShows = (bandId: string | undefined): UseQueryResult<Show[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'shows'],
    queryFn: () => list_band_shows(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch musicians for a specific band
 * @param bandId - The band ID to fetch musicians for
 * @returns UseQueryResult with musicians data, loading, and error states
 */
export const useBandMusicians = (bandId: string | undefined): UseQueryResult<Musician[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'musicians'],
    queryFn: () => view_band_musicians(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch genres for a specific band
 * @param bandId - The band ID to fetch genres for
 * @returns UseQueryResult with genres data, loading, and error states
 */
export const useBandGenres = (bandId: string | undefined): UseQueryResult<BandGenre[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'genres'],
    queryFn: () => list_band_genres(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show comments for a specific band
 * @param bandId - The band ID to fetch show comments for
 * @returns UseQueryResult with show comments data, loading, and error states
 */
export const useBandShowComments = (bandId: string | undefined): UseQueryResult<ShowComment[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'show-comments'],
    queryFn: () => list_band_show_comments(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show media for a specific band
 * @param bandId - The band ID to fetch show media for
 * @returns UseQueryResult with show media data, loading, and error states
 */
export const useBandShowMedia = (bandId: string | undefined): UseQueryResult<ShowMedia[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'show-media'],
    queryFn: () => list_band_show_media(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch bands for a specific musician
 * @param musicianId - The musician ID to fetch bands for
 * @returns UseQueryResult with bands data, loading, and error states
 */
export const useMusicianBands = (musicianId: string | undefined): UseQueryResult<Band[], Error> => {
  return useQuery({
    queryKey: ['musician', musicianId, 'bands'],
    queryFn: () => list_musician_bands(musicianId!),
    enabled: !!musicianId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch shows for a specific musician
 * @param musicianId - The musician ID to fetch shows for
 * @returns UseQueryResult with shows data, loading, and error states
 */
export const useMusicianShows = (musicianId: string | undefined): UseQueryResult<Show[], Error> => {
  return useQuery({
    queryKey: ['musician', musicianId, 'shows'],
    queryFn: () => list_musician_shows(musicianId!),
    enabled: !!musicianId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show comments for a specific musician
 * @param musicianId - The musician ID to fetch show comments for
 * @returns UseQueryResult with show comments data, loading, and error states
 */
export const useMusicianShowComments = (musicianId: string | undefined): UseQueryResult<ShowComment[], Error> => {
  return useQuery({
    queryKey: ['musician', musicianId, 'show-comments'],
    queryFn: () => list_musician_show_comments(musicianId!),
    enabled: !!musicianId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show media for a specific musician
 * @param musicianId - The musician ID to fetch show media for
 * @returns UseQueryResult with show media data, loading, and error states
 */
export const useMusicianShowMedia = (musicianId: string | undefined): UseQueryResult<ShowMedia[], Error> => {
  return useQuery({
    queryKey: ['musician', musicianId, 'show-media'],
    queryFn: () => list_musician_show_media(musicianId!),
    enabled: !!musicianId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch custom links for a specific musician
 * @param musicianId - The musician ID to fetch links for
 * @returns UseQueryResult with links data, loading, and error states
 */
export const useMusicianLinks = (musicianId: string | undefined): UseQueryResult<MusicianLink[], Error> => {
  return useQuery({
    queryKey: ['musician', musicianId, 'links'],
    queryFn: () => list_musician_links(musicianId!),
    enabled: !!musicianId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch shows for a specific venue
 * @param venueId - The venue ID to fetch shows for
 * @returns UseQueryResult with shows data, loading, and error states
 */
export const useVenueShows = (venueId: string | undefined): UseQueryResult<Show[], Error> => {
  return useQuery({
    queryKey: ['venue', venueId, 'shows'],
    queryFn: () => list_venue_shows(venueId!),
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show comments for a specific venue
 * @param venueId - The venue ID to fetch show comments for
 * @returns UseQueryResult with show comments data, loading, and error states
 */
export const useVenueShowComments = (venueId: string | undefined): UseQueryResult<ShowComment[], Error> => {
  return useQuery({
    queryKey: ['venue', venueId, 'show-comments'],
    queryFn: () => list_venue_show_comments(venueId!),
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch show media for a specific venue
 * @param venueId - The venue ID to fetch show media for
 * @returns UseQueryResult with show media data, loading, and error states
 */
export const useVenueShowMedia = (venueId: string | undefined): UseQueryResult<ShowMedia[], Error> => {
  return useQuery({
    queryKey: ['venue', venueId, 'show-media'],
    queryFn: () => list_venue_show_media(venueId!),
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch bands that have played at a specific venue
 * @param venueId - The venue ID to fetch bands for
 * @returns UseQueryResult with bands data, loading, and error states
 */
export const useVenueBands = (venueId: string | undefined): UseQueryResult<Band[], Error> => {
  return useQuery({
    queryKey: ['venue', venueId, 'bands'],
    queryFn: () => list_venue_bands(venueId!),
    enabled: !!venueId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch all Firebase users (admin only)
 * @returns UseQueryResult with users data, loading, and error states
 */
export const useFirebaseUsers = (): UseQueryResult<FirebaseUser[], Error> => {
  return useQuery({
    queryKey: ['firebase-users'],
    queryFn: async () => {
      const result = await list_users();
      return result.users;
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * React Query hook to fetch show contributors for a specific band
 * @param bandId - The band ID to fetch show contributors for
 * @returns UseQueryResult with show contributors data, loading, and error states
 */
export const useBandShowContributors = (bandId: string | undefined): UseQueryResult<ShowContributorRecord[], Error> => {
  return useQuery({
    queryKey: ['band', bandId, 'show-contributors'],
    queryFn: () => list_show_contributors(bandId!),
    enabled: !!bandId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to check which bands a user is a show contributor for.
 * An empty array means "not a contributor for any band" - use
 * `(data?.length ?? 0) > 0` to decide whether to show the badge.
 * @param userId - The user's Firebase uid to check
 * @param enabled - Pass !!user from useAuth() too - the endpoint requires a
 *   logged-in caller, so this can't run for an anonymous viewer.
 */
export const useContributorBands = (userId: string | undefined, enabled: boolean = true): UseQueryResult<Band[], Error> => {
  return useQuery({
    queryKey: ['contributorBands', userId],
    queryFn: () => list_contributor_bands(userId!),
    enabled: enabled && !!userId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch the current user's linked musician profile
 * @param enabled - Whether to enable the query (e.g., only when user is authenticated as musician)
 * @returns UseQueryResult with musician data, loading, and error states
 */
export const useMyMusician = (enabled: boolean = true): UseQueryResult<Musician | null, Error> => {
  return useQuery({
    queryKey: ['my-musician'],
    queryFn: get_my_musician,
    enabled,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: false, // Don't retry on 404
  });
};

/**
 * React Query hook to fetch a fan's public profile
 * @param uid - The user ID to fetch the profile for
 * @returns UseQueryResult with fan profile data, loading, and error states
 */
export const useFanProfile = (uid: string | undefined): UseQueryResult<FanProfile, Error> => {
  return useQuery({
    queryKey: ['fan', uid],
    queryFn: () => get_fan_profile(uid!),
    enabled: !!uid,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * React Query hook to fetch the network graph of musicians and bands
 * @returns UseQueryResult with network graph data, loading, and error states
 */
export const useNetworkGraph = (): UseQueryResult<NetworkGraphResponse, Error> => {
  return useQuery({
    queryKey: ['network-graph'],
    queryFn: get_network_graph,
    staleTime: 1000 * 60 * 10, // 10 minutes - graph changes less frequently
    gcTime: 1000 * 60 * 30, // 30 minutes
  });
};
